import React from 'react';
import { ChevronDown, Sparkles } from 'lucide-react';
import { cn } from '../../lib/utils';

const ACCENT = {
  gold: 'de-treasury-accent-gold',
  refund: 'de-treasury-accent-refund',
  payout: 'de-treasury-accent-payout',
  amber: 'de-treasury-accent-amber',
};

export function TreasuryUltraShell({
  title,
  subtitle,
  icon,
  accent = 'gold',
  children,
  banner,
  footer,
  scrollable = false,
  scrollMaxHeightClass = 'max-h-[min(340px,44vh)]',
  showScrollHint = false,
  scrollHint = 'Scroll inside panel',
  className,
  badge,
}) {
  return (
    <div className={cn('de-treasury-ultra-panel', ACCENT[accent] || ACCENT.gold, className)}>
      <div className="de-treasury-ultra-glow" aria-hidden />
      <div className="relative z-[1] flex flex-col p-4 sm:p-5">
        <div className="mb-4 flex shrink-0 flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 items-start gap-3">
            {icon ? (
              <span className="de-treasury-ultra-icon" aria-hidden>
                {icon}
              </span>
            ) : null}
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="m-0 text-sm font-bold tracking-wide text-de-gold sm:text-base">{title}</h3>
                {badge != null ? (
                  <span className="de-treasury-ultra-badge">{badge}</span>
                ) : null}
              </div>
              {subtitle ? (
                <p className="m-0 mt-1 text-[11px] leading-relaxed text-de-muted">{subtitle}</p>
              ) : null}
            </div>
          </div>
          <Sparkles className="h-4 w-4 shrink-0 text-de-gold/35" aria-hidden />
        </div>

        {banner ? <div className="mb-3 shrink-0">{banner}</div> : null}

        {scrollable ? (
          <div className="de-ultra-roll-wrap relative min-h-0">
            <div className={cn('de-ultra-roll de-treasury-ultra-scroll space-y-3 pr-0.5', scrollMaxHeightClass)}>
              {children}
            </div>
          </div>
        ) : (
          <div className="space-y-3">{children}</div>
        )}

        {showScrollHint && scrollable ? (
          <p className="de-ultra-roll-hint m-0 mt-3 flex items-center justify-center gap-1 text-[10px] font-bold uppercase tracking-[0.15em] text-de-gold/45">
            <ChevronDown className="h-3 w-3 animate-bounce" aria-hidden />
            {scrollHint}
          </p>
        ) : null}

        {footer ? <div className="mt-4 shrink-0 border-t border-de-gold/15 pt-3">{footer}</div> : null}
      </div>
    </div>
  );
}

export function TreasuryUltraField({ label, hint, children, className }) {
  return (
    <label className={cn('de-treasury-ultra-field block', className)}>
      <span className="de-treasury-ultra-label">{label}</span>
      {children}
      {hint ? <span className="de-treasury-ultra-field-hint">{hint}</span> : null}
    </label>
  );
}

export function TreasuryUltraSelect(props) {
  return <select {...props} className={cn('de-treasury-ultra-select', props.className)} />;
}

export function TreasuryUltraInput(props) {
  return <input {...props} className={cn('de-treasury-ultra-input', props.className)} />;
}

export function TreasuryUltraAlert({ tone = 'info', children }) {
  return (
    <div className={cn('de-treasury-ultra-alert', `de-treasury-ultra-alert--${tone}`)}>
      {children}
    </div>
  );
}

export function TreasuryUltraBtn({ variant = 'gold', className, children, type = 'button', ...props }) {
  return (
    <button
      type={type}
      {...props}
      className={cn('de-treasury-ultra-btn', `de-treasury-ultra-btn--${variant}`, className)}
    >
      {children}
    </button>
  );
}

export function TreasuryUltraStat({ label, value, sub, tone = 'gold' }) {
  return (
    <div className={cn('de-treasury-ultra-stat', `de-treasury-ultra-stat--${tone}`)}>
      <p className="de-treasury-ultra-stat-label">{label}</p>
      <p className="de-treasury-ultra-stat-value">{value}</p>
      {sub ? <p className="de-treasury-ultra-stat-sub">{sub}</p> : null}
    </div>
  );
}

export function TreasuryUltraRow({ title, meta, actions, tone = 'amber' }) {
  return (
    <div className={cn('de-treasury-ultra-row', `de-treasury-ultra-row--${tone}`)}>
      <div className="w-full min-w-0">
        <p className="de-treasury-ultra-row-title">{title}</p>
        {meta ? <p className="de-treasury-ultra-row-meta">{meta}</p> : null}
      </div>
      {actions ? <div className="flex w-full flex-wrap justify-end gap-2">{actions}</div> : null}
    </div>
  );
}
